import { useEffect, useState, useRef } from "react";
import "@/App.css";
import axios from "axios";
import {
  Phone, Mail, Clock, Menu, X, ArrowRight, ArrowUpRight, Check,
  Layers, Footprints, TreePine, Square, Box, Slice, FileStack, Droplets, MessageCircle, PhoneCall
} from "lucide-react";
import { translations, CONTACT_INFO, IMAGES, PRODUCT_IMAGES, GALLERY } from "@/i18n";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PRODUCT_ICONS = [Layers, Footprints, TreePine, Square, Box, Droplets, Slice, FileStack];

function useScrollSpy() {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return scrolled;
}

function useReveal() {
  const refs = useRef([]);
  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && e.target.classList.add("in")),
      { threshold: 0.12 }
    );
    refs.current.forEach((el) => el && obs.observe(el));
    return () => obs.disconnect();
  }, []);
  return (el) => { if (el && !refs.current.includes(el)) refs.current.push(el); };
}

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

/* -------------------- NAV -------------------- */
function Nav({ lang, setLang, t }) {
  const scrolled = useScrollSpy();
  const [open, setOpen] = useState(false);
  const go = (id) => { setOpen(false); scrollTo(id); };

  return (
    <nav className={`nav ${scrolled ? "scrolled" : ""}`} data-testid="main-nav">
      <div className="nav-inner">
        <div className="logo" onClick={() => go("home")} data-testid="logo">
          <div className="logo-mark">D</div>
          <div>
            <div className="logo-text">D Design</div>
            <div className="logo-sub">Creations · Carpentry</div>
          </div>
        </div>

        <div className={`nav-links ${open ? "open" : ""}`}>
          <button className="nav-link" onClick={() => go("about")} data-testid="nav-about">{t.nav.about}</button>
          <button className="nav-link" onClick={() => go("products")} data-testid="nav-products">{t.nav.products}</button>
          <button className="nav-link" onClick={() => go("services")} data-testid="nav-services">{t.nav.services}</button>
          <button className="nav-link" onClick={() => go("gallery")} data-testid="nav-gallery">{t.nav.gallery}</button>
          <button className="nav-link" onClick={() => go("process")} data-testid="nav-process">{t.nav.process}</button>
          <button className="nav-link" onClick={() => go("contact")} data-testid="nav-contact">{t.nav.contact}</button>

          <div className="lang-switch" data-testid="lang-switch">
            <button className={`lang-btn ${lang === "el" ? "active" : ""}`} onClick={() => setLang("el")} data-testid="lang-el">EL</button>
            <button className={`lang-btn ${lang === "en" ? "active" : ""}`} onClick={() => setLang("en")} data-testid="lang-en">EN</button>
            <button className={`lang-btn ${lang === "bg" ? "active" : ""}`} onClick={() => setLang("bg")} data-testid="lang-bg">BG</button>
          </div>

          <button className="btn-primary" onClick={() => go("contact")} data-testid="nav-quote-btn">
            <span>{t.nav.quote}</span> <ArrowRight size={15} />
          </button>
        </div>

        <button className="mobile-toggle" onClick={() => setOpen(!open)} data-testid="mobile-toggle" aria-label="menu">
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>
    </nav>
  );
}

/* -------------------- HERO -------------------- */
function Hero({ t }) {
  return (
    <section className="hero grain" id="home" data-testid="hero-section">
      <div className="hero-bg-num" aria-hidden="true">D</div>
      <div className="hero-inner">
        <div className="hero-grid">
          <div>
            <span className="eyebrow" data-testid="hero-eyebrow">{t.hero.eyebrow}</span>
            <h1 className="hero-title" data-testid="hero-title">
              {t.hero.title1} <em>{t.hero.title2}</em><br />{t.hero.title3}
            </h1>
            <p className="hero-sub" data-testid="hero-sub">{t.hero.subtitle}</p>
            <div className="hero-cta">
              <button className="btn-primary" onClick={() => scrollTo("contact")} data-testid="hero-cta-primary">
                <span>{t.hero.ctaPrimary}</span> <ArrowRight size={15} />
              </button>
              <button className="btn-outline" onClick={() => scrollTo("products")} data-testid="hero-cta-secondary">
                <span>{t.hero.ctaSecondary}</span>
              </button>
            </div>
          </div>
          <div className="hero-art">
            <img src={IMAGES.hero} alt="D Design timber showroom" />
            <div className="hero-stamp" aria-hidden="true">
              <div className="hero-stamp-inner">
                <svg viewBox="0 0 144 144">
                  <defs>
                    <path id="circle" d="M 72,72 m -56,0 a 56,56 0 1,1 112,0 a 56,56 0 1,1 -112,0" />
                  </defs>
                  <text fontFamily="Inter, sans-serif" fontSize="9.4" fontWeight="500" letterSpacing="4" fill="#5c3a1c">
                    <textPath href="#circle">
                      {`PREMIUM TIMBER · D DESIGN · SINCE 2010 · `}
                    </textPath>
                  </text>
                </svg>
                <span className="hero-stamp-center">D</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* -------------------- MARQUEE -------------------- */
function Marquee({ items }) {
  const doubled = [...items, ...items];
  return (
    <div className="marquee" data-testid="marquee">
      <div className="marquee-track">
        {doubled.map((it, i) => (
          <span key={i} className="marquee-item">{it}</span>
        ))}
      </div>
    </div>
  );
}

/* -------------------- ABOUT -------------------- */
function About({ t, register }) {
  return (
    <section className="section" id="about" data-testid="about-section">
      <div className="container">
        <div className="about-grid">
          <div className="about-imgs fade-up" ref={register}>
            <div className="about-img-main">
              <img src={IMAGES.aboutMain} alt="CNC cutting workshop" />
            </div>
            <div className="about-img-sub">
              <img src={IMAGES.aboutSub} alt="Timber selection" />
            </div>
            <div className="about-numbers">
              <div className="about-numbers-big">{t.about.statBig}</div>
              <div className="about-numbers-lbl">{t.about.statLbl}</div>
            </div>
          </div>

          <div className="about-text fade-up" ref={register}>
            <span className="eyebrow">{t.about.eyebrow}</span>
            <h2 className="section-title" data-testid="about-title">
              {t.about.title1} <em>{t.about.title2}</em>
            </h2>
            <p>{t.about.p1}</p>
            <p>{t.about.p2}</p>
            <ul className="about-bullets" data-testid="about-bullets">
              <li><Check size={18} /> {t.about.bullet1}</li>
              <li><Check size={18} /> {t.about.bullet2}</li>
              <li><Check size={18} /> {t.about.bullet3}</li>
              <li><Check size={18} /> {t.about.bullet4}</li>
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

/* -------------------- PRODUCTS -------------------- */
function Products({ t, register }) {
  return (
    <section className="section tan" id="products" data-testid="products-section">
      <div className="container">
        <div className="section-head fade-up" ref={register}>
          <span className="eyebrow">{t.products.eyebrow}</span>
          <h2 className="section-title">
            {t.products.title1} <em>{t.products.title2}</em>
          </h2>
          <p className="section-sub">{t.products.subtitle}</p>
        </div>

        <div className="products-grid">
          {t.products.items.map((p, i) => {
            const Icon = PRODUCT_ICONS[i] || Layers;
            return (
              <div key={i} className="product-card fade-up" ref={register} data-testid={`product-card-${i}`}>
                <div className="product-img">
                  <img src={PRODUCT_IMAGES[i]} alt={p.name} loading="lazy" />
                </div>
                <div className="product-body">
                  <div className="product-head">
                    <div className="product-num">0{i + 1}</div>
                    <Icon size={22} strokeWidth={1.4} className="product-icon" />
                  </div>
                  <h3 className="product-name">{p.name}</h3>
                  <p className="product-desc">{p.desc}</p>
                  <div className="product-arrow"><ArrowUpRight size={20} strokeWidth={1.3} /></div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* -------------------- SERVICES -------------------- */
function Services({ t, register }) {
  return (
    <section className="section" id="services" data-testid="services-section">
      <div className="container">
        <div className="section-head fade-up" ref={register}>
          <span className="eyebrow">{t.services.eyebrow}</span>
          <h2 className="section-title">
            {t.services.title1} <em>{t.services.title2}</em>
          </h2>
          <p className="section-sub">{t.services.subtitle}</p>
        </div>

        <div className="services-list">
          {t.services.items.map((s, i) => (
            <div key={i} className="service-row" data-testid={`service-card-${i}`} ref={register}>
              <div className="service-num">— {s.num}</div>
              <div className="service-name">{s.title}</div>
              <div className="service-desc">{s.desc}</div>
              <div className="service-arrow"><ArrowUpRight size={26} strokeWidth={1.2} /></div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* -------------------- GALLERY -------------------- */
function Gallery({ t, register }) {
  const [filter, setFilter] = useState("all");
  const cats = t.gallery.categories;
  const items = filter === "all" ? GALLERY : GALLERY.filter((g) => g.cat === filter);

  return (
    <section className="section tan" id="gallery" data-testid="gallery-section">
      <div className="container">
        <div className="gallery-head fade-up" ref={register}>
          <div>
            <span className="eyebrow">{t.gallery.eyebrow}</span>
            <h2 className="section-title">
              {t.gallery.title1} <em>{t.gallery.title2}</em>
            </h2>
          </div>
          <div className="gallery-filters" data-testid="gallery-filters">
            {Object.entries(cats).map(([k, label]) => (
              <button
                key={k}
                className={`filter-btn ${filter === k ? "active" : ""}`}
                onClick={() => setFilter(k)}
                data-testid={`filter-${k}`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        <div className="gallery-grid">
          {items.map((g, i) => {
            const cap = t.gallery.captions[g.key];
            return (
              <div
                key={g.key}
                className={`gallery-item ${filter === "all" ? g.cls : ""}`}
                data-testid={`gallery-item-${i}`}
              >
                <img src={g.src} alt={cap?.name || g.key} loading="lazy" />
                <div className="gallery-cap">
                  {cap?.name}
                  <span className="gallery-cap-sub">{cap?.cat}</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* -------------------- PROCESS -------------------- */
function Process({ t, register }) {
  return (
    <section className="section dark grain" id="process" data-testid="process-section">
      <div className="container">
        <div className="section-head fade-up" ref={register}>
          <span className="eyebrow">{t.process.eyebrow}</span>
          <h2 className="section-title">
            {t.process.title1} <em>{t.process.title2}</em>
          </h2>
          <p className="section-sub">{t.process.subtitle}</p>
        </div>

        <div className="process-grid">
          {t.process.steps.map((s, i) => (
            <div key={i} className="process-step fade-up" ref={register} data-testid={`process-step-${i}`}>
              <div className="process-num">0{i + 1}</div>
              <h3 className="process-name">{s.name}</h3>
              <p className="process-text">{s.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* -------------------- CONTACT -------------------- */
function Contact({ t, register }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [status, setStatus] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");

  const handle = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    setErrorMsg("");
    try {
      const payload = { ...form };
      if (!payload.phone) delete payload.phone;
      if (!payload.subject) delete payload.subject;
      await axios.post(`${API}/contact`, payload);
      setStatus("success");
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      setStatus("error");
      setErrorMsg(err?.response?.data?.detail?.[0]?.msg || t.contact.error);
    }
  };

  return (
    <section className="section" id="contact" data-testid="contact-section">
      <div className="container">
        <div className="section-head fade-up" ref={register}>
          <span className="eyebrow">{t.contact.eyebrow}</span>
          <h2 className="section-title">
            {t.contact.title1} <em>{t.contact.title2}</em>
          </h2>
          <p className="section-sub">{t.contact.subtitle}</p>
        </div>

        <div className="contact-grid">
          <div className="contact-info fade-up" ref={register}>
            <a className="contact-row" href={`tel:${CONTACT_INFO.phoneRaw}`} data-testid="contact-phone">
              <div className="contact-icon"><Phone size={18} /></div>
              <div>
                <div className="contact-label">{t.contact.phoneLabel}</div>
                <div className="contact-val">{CONTACT_INFO.phone}</div>
              </div>
            </a>
            <a className="contact-row" href={`mailto:${CONTACT_INFO.email}`} data-testid="contact-email">
              <div className="contact-icon"><Mail size={18} /></div>
              <div>
                <div className="contact-label">{t.contact.emailLabel}</div>
                <div className="contact-val" style={{ fontSize: 17, wordBreak: 'break-all' }}>{CONTACT_INFO.email}</div>
              </div>
            </a>
            <div className="contact-row" data-testid="contact-hours">
              <div className="contact-icon"><Clock size={18} /></div>
              <div>
                <div className="contact-label">{t.contact.hoursLabel}</div>
                <div className="contact-val" style={{ fontSize: 17 }}>{t.contact.hours}</div>
              </div>
            </div>
          </div>

          <div className="form-card fade-up" ref={register}>
            <form className="form" onSubmit={submit} data-testid="contact-form">
              <div className="form-row">
                <div className="field">
                  <label htmlFor="name">{t.contact.name}</label>
                  <input id="name" required minLength={2} value={form.name} onChange={handle("name")} data-testid="form-name" />
                </div>
                <div className="field">
                  <label htmlFor="email">{t.contact.email}</label>
                  <input id="email" type="email" required value={form.email} onChange={handle("email")} data-testid="form-email" />
                </div>
              </div>
              <div className="form-row">
                <div className="field">
                  <label htmlFor="phone">{t.contact.phone}</label>
                  <input id="phone" value={form.phone} onChange={handle("phone")} data-testid="form-phone" />
                </div>
                <div className="field">
                  <label htmlFor="subject">{t.contact.subject}</label>
                  <input id="subject" value={form.subject} onChange={handle("subject")} data-testid="form-subject" />
                </div>
              </div>
              <div className="field">
                <label htmlFor="message">{t.contact.message}</label>
                <textarea id="message" required minLength={5} value={form.message} onChange={handle("message")} data-testid="form-message" />
              </div>

              {status === "success" && <div className="form-msg success" data-testid="form-success">{t.contact.success}</div>}
              {status === "error" && <div className="form-msg error" data-testid="form-error">{errorMsg || t.contact.error}</div>}

              <button type="submit" className="btn-primary" disabled={status === "sending"} data-testid="form-submit" style={{ justifySelf: 'start', marginTop: 8 }}>
                <span>{status === "sending" ? t.contact.sending : t.contact.send}</span> <ArrowRight size={15} />
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}

/* -------------------- FOOTER -------------------- */
function Footer({ t }) {
  return (
    <footer className="footer" data-testid="footer">
      <div className="container">
        <div className="footer-top">
          <div>
            <div className="footer-brand-large">
              {t.footer.brand1}<br /><em>{t.footer.brand2}</em>
            </div>
            <p className="footer-tag">{t.footer.tagline}</p>
          </div>
          <div>
            <h4>{t.footer.quick}</h4>
            <ul>
              <li><button onClick={() => scrollTo("about")}>{t.nav.about}</button></li>
              <li><button onClick={() => scrollTo("products")}>{t.nav.products}</button></li>
              <li><button onClick={() => scrollTo("services")}>{t.nav.services}</button></li>
              <li><button onClick={() => scrollTo("gallery")}>{t.nav.gallery}</button></li>
              <li><button onClick={() => scrollTo("process")}>{t.nav.process}</button></li>
              <li><button onClick={() => scrollTo("contact")}>{t.nav.contact}</button></li>
            </ul>
          </div>
          <div>
            <h4>{t.footer.reach}</h4>
            <ul>
              <li><a href={`tel:${CONTACT_INFO.phoneRaw}`}>{CONTACT_INFO.phone}</a></li>
              <li><a href={`mailto:${CONTACT_INFO.email}`}>{CONTACT_INFO.email}</a></li>
            </ul>
          </div>
        </div>
        <div className="footer-bottom">
          <div>© {new Date().getFullYear()} D Design Creations Carpentry · {t.footer.rights}</div>
          <div style={{ fontFamily: "'Fraunces', serif", fontStyle: 'italic', fontSize: 14, color: 'var(--oak)' }}>Made by hand.</div>
        </div>
      </div>
    </footer>
  );
}

/* -------------------- BRAND BANNER (TOP, WIDE) -------------------- */
function BrandBanner({ lang }) {
  const tag = lang === "el"
    ? "— Καλώς ήρθατε —"
    : lang === "bg"
    ? "— Добре дошли —"
    : "— Welcome —";
  return (
    <section className="brand-banner grain" data-testid="brand-banner">
      <div className="brand-banner-inner">
        <div className="brand-banner-tag">{tag}</div>
        <img
          src={IMAGES.bannerEl}
          alt="D Creations Design Carpentry — wooden banner"
          className="brand-banner-img"
          data-testid="brand-banner-img"
        />
      </div>
    </section>
  );
}

/* -------------------- BRAND SHOWCASE (LOWER, SQUARE LOGO) -------------------- */
function BrandShowcase({ lang }) {
  const logoSrc = lang === "el" ? IMAGES.woodLogoEl : IMAGES.woodLogo;
  const tag = lang === "el"
    ? "— Το σήμα μας, σκαλισμένο στο ξύλο —"
    : lang === "bg"
    ? "— Нашият знак, гравиран в дървото —"
    : "— Our mark, carved in wood —";
  return (
    <section className="brand-showcase grain" data-testid="brand-showcase">
      <div className="brand-showcase-inner">
        <div className="brand-showcase-tag">{tag}</div>
        <img
          src={logoSrc}
          alt="D Design Creations Carpentry — wood logo"
          className="brand-showcase-img"
          data-testid="brand-logo-large"
        />
      </div>
    </section>
  );
}

/* -------------------- APP -------------------- */
function App() {
  const [lang, setLang] = useState("el");
  const t = translations[lang];
  const register = useReveal();

  useEffect(() => {
    document.documentElement.lang = lang;
    document.title = lang === "el"
      ? "D Design Creations Carpentry · Πρώτες ύλες ξυλείας"
      : lang === "bg"
      ? "D Design Creations Carpentry · Премиум дървесина"
      : "D Design Creations Carpentry · Premium Timber Supplier";
  }, [lang]);

  return (
    <div className="App">
      <Nav lang={lang} setLang={setLang} t={t} />
      <BrandBanner lang={lang} />
      <Hero t={t} />
      <Marquee items={t.marquee} />
      <About t={t} register={register} />
      <Products t={t} register={register} />
      <Services t={t} register={register} />
      <Gallery t={t} register={register} />
      <BrandShowcase lang={lang} />
      <Process t={t} register={register} />
      <Contact t={t} register={register} />
      <Footer t={t} />

      <div className="floating-contact" data-testid="floating-contact">
        <a
          href={`viber://chat?number=${CONTACT_INFO.phoneRaw}`}
          className="fab fab-viber"
          aria-label="Viber"
          data-testid="viber-fab"
        >
          <PhoneCall size={24} strokeWidth={1.8} />
          <span className="fab-pulse" />
          <span className="fab-tooltip">Viber</span>
        </a>
        <a
          href={`https://wa.me/${CONTACT_INFO.phoneRaw.replace('+', '')}?text=${encodeURIComponent(lang === 'el' ? 'Γεια σας! Ενδιαφέρομαι για ξυλεία.' : lang === 'bg' ? 'Здравейте! Интересувам се от вашите дървени материали.' : 'Hello! I am interested in your timber products.')}`}
          target="_blank"
          rel="noopener noreferrer"
          className="fab fab-whatsapp"
          aria-label="WhatsApp"
          data-testid="whatsapp-fab"
        >
          <MessageCircle size={26} strokeWidth={1.8} />
          <span className="fab-pulse" />
          <span className="fab-tooltip">WhatsApp</span>
        </a>
      </div>
    </div>
  );
}

export default App;
